/**
 * Notifier Container.
 * It helps to manage the actions for Loading, Error Block and Success Block.
 * Its completely resuable Container. Update your action in `Notifier` Reducer with your paylod for overriding behaviour.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { Link, Info, SpinLoader } from '../components/common/index';
import { locationUpdate } from '../actions/action-contact-location';
import { NOTIFIER_DETAILS } from '../constants/PropTypesShapes';
import Page from '../../lib/page';
import * as emailLink from '../../../data/emailFormURL.json';
import i18n from '../../lib/reacti18n';

const info = {
  links: {
    callCustomerService: i18n.message('smarthelp/info.info.links.callCustomerService'),
    goToMessageCenter: i18n.message('smarthelp/info.info.links.goToMessageCenter'),
    goToMessageCenterUrl: i18n.message('smarthelp/info.info.links.goToMessageCenterUrl'),
    viewYourAccount: i18n.message('smarthelp/info.info.links.viewYourAccount'),
    viewYourAccountUrl: i18n.message('smarthelp/info.info.links.viewYourAccountUrl'),
    visitTheHelpCenter: i18n.message('smarthelp/info.info.links.visitTheHelpCenter'),
    visitTheHelpCenterUrl: i18n.message('smarthelp/info.info.links.visitTheHelpCenterUrl'),
    visitTheCommunityForum: i18n.message('smarthelp/info.info.links.visitTheCommunityForum'),
  },
  title: {
    pageIsUnavailable: i18n.message('smarthelp/info.info.title.pageIsUnavailable'),
    functionIsUnavailable: i18n.message('smarthelp/info.info.title.functionIsUnavailable'),
    emailSent: i18n.message('smarthelp/info.info.title.emailSent'),
  },
  desc: {
    tryAgain: i18n.message('smarthelp/info.info.desc.tryAgain'),
    emailSentNotes: i18n.message('smarthelp/info.info.desc.emailSentNotes'),
    nonSmcEmailSentNotes: i18n.message('smarthelp/info.info.desc.nonSmcEmailSentNotes'),
    emailSentAdditionalResourcesNotes: i18n.message('smarthelp/info.info.desc.emailSentAdditionalResourcesNotes'),
    emailSentNotesForComplaintMarkets: i18n.message('smarthelp/info.info.desc.emailSentNotesForComplaintMarkets'),
    nonSmcEmailSentNotesForComplaintMarkets: i18n.message(
      'smarthelp/info.info.desc.nonSmcEmailSentNotesForComplaintMarkets',
    ),
    ppgfEmailSentNotes: i18n.message('smarthelp/info.info.desc.ppgfEmailSentNotes'),
  },
};
const communityUrl = Page.getCommunityLink();

class Notifier extends Component {
  static getLinkUrl(url) {
    if (url === 'visitTheCommunityForumUrl') {
      return communityUrl;
    }
    return emailLink[url];
  }

  // Check whether display item or not
  static notDisplayLink(item) {
    const noCommunityLink = item.href === 'visitTheCommunityForumUrl' && !communityUrl;
    return item.display === false || noCommunityLink;
  }

  componentDidMount() {
    // scroll to the top of the page when email sent shown
    window.scrollTo(0, 0);
  }

  // This handles all the Link Custom Events served by the Container.
  handleLinkAction(actonType) {
    // eslint-disable-line class-methods-use-this
    switch (actonType) {
      case 'callUs':
        return () => {
          this.props.locationUpdate({ pageName: 'callUs' });
        };
      default:
        return () => {};
    }
  }

  // Return Container and its display is conditional by `display` props.
  getContainer() {
    let notifyContainer = null;

    if (this.props.display) {
      notifyContainer = (
        <div className="notify-container col-xs-12 col-sm-12 col-md-12 col-lg-12">
          {this.getNotifier(this.props.type)}
        </div>
      );
    }

    return notifyContainer;
  }

  // Return Link Component as per `href` props value.
  getLinkItems(item) {
    // Return Null Only if `display` is defined and set to `false`.
    if (Notifier.notDisplayLink(item)) {
      return null;
    }
    let link = null;

    if (item.href) {
      link = (
        // Simple `href` anchor Element served by Link Component.
        <Link
          key={item.label}
          id={item.label}
          label={info.links[item.label]}
          href={Notifier.getLinkUrl(item.href)}
          trackLink={item.trackLink}
        />
      );
    } else {
      // button Element served by Link Component with onClick Event.
      link = (
        <Link
          key={item.label}
          id={item.label}
          label={info.links[item.label]}
          onClick={this.handleLinkAction(item.type)}
          trackLink={item.trackLink}
        />
      );
    }

    // Wrapped with `li` element.
    return <li>{link}</li>;
  }

  // Get List of Links Block as per `details` props.
  getLinkBlock() {
    let linkBlock = null;
    const linkDetail = _get(this.props.details, 'links', {});
    const linkClassName = _get(linkDetail, 'className', '');
    const linkItems = _get(linkDetail, 'items', []);

    if (!_isEmpty(linkItems)) {
      linkBlock = (
        <div className={linkClassName}>
          <ul className="list-unstyled">{linkItems.map((item) => this.getLinkItems(item))}</ul>
        </div>
      );
    }

    return linkBlock;
  }

  // Return Component as per `type` props.
  // 1. Loader Component
  // 2. Info Component with Icon for Error Block.
  // 3. Info Component without Icon for Success Block.
  getNotifier(type) {
    switch (type) {
      // Case for Loading Page Level `Loader`.
      case 'loader':
        return <SpinLoader show />;
      // Case for `Error Page` with Icon with Customize Option.
      case 'error':
        return (
          <div className={this.props.type}>
            <Info
              iconClass={this.props.details.iconClass}
              title={info.title[this.props.details.title]}
              description={info.desc[this.props.details.description]}
            />
            {this.getLinkBlock()}
          </div>
        );
      // Case for `Success Page` with Icon with Customize Option.
      case 'info':
        return (
          <div className={this.props.type}>
            <Info
              title={info.title[this.props.details.title]}
              description={info.desc[this.props.details.description]}
            />
            {this.getLinkBlock()}
          </div>
        );
      default:
        return <div />;
    }
  }

  render() {
    return this.getContainer();
  }
}

// Get States from `Notifier` Reducer.
function mapStateToProps(states) {
  return {
    type: states.notifier.type,
    details: states.notifier.details,
    display: states.notifier.display,
  };
}

// Dispatch to Props.
function mapDispatchToProps(dispatch) {
  return bindActionCreators({ locationUpdate }, dispatch);
}

// Notifier Props Types.
Notifier.propTypes = {
  type: PropTypes.string,
  details: PropTypes.shape(NOTIFIER_DETAILS),
  display: PropTypes.bool,
  locationUpdate: PropTypes.func.isRequired,
};

// Notifier Props default values.
Notifier.defaultProps = {
  type: '',
  details: {},
  display: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(Notifier);
